import * as React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { rolesMessages } from 'src/features/roles/utils/messages';
import { PERMISSION } from 'src/features/auth/types';
import { GET_ROLE_RESOURCE, useGetRole } from 'src/features/roles/api/useGetRole';
import { useUpdateRole } from 'src/features/roles/api/useUpdateRole';
import { toast } from 'src/hooks/useToast';
import PermissionBlock from 'src/features/roles/components/PermissionBlock';
import {
    BILLING_SETTINGS_SECTION_PERMISSION,
    CUSTOMERS_PERMISSION,
    DISCOUNTS_SECTION_PERMISSION,
    FINANCIALS_SECTION_PERMISSION,
    SUBSCRIPTIONS_SECTION_PERMISSION,
    USERS_SECTIONS_PERMISSION,
} from 'src/features/roles/utils/constants';
import { customersMessages } from 'src/features/customers/utils/messages';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { discountsMessages } from 'src/features/discounts/utils/messages';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import { billingMessages } from 'src/features/billing/utils/messages';
import { GET_PERMISSIONS_RESOURCE } from 'src/features/auth/api/useGetPermissions';

function RoleDashboard() {
    const { formatMessage } = useIntl();
    const { roleId = '' } = useParams();
    const { data } = useGetRole({ roleId, config: { enabled: !!roleId } });
    const { mutate } = useUpdateRole({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [GET_ROLE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_PERMISSIONS_RESOURCE] });

            toast({
                title: formatMessage(rolesMessages.rolePermissionsChanged),
                description: formatMessage(rolesMessages.rolePermissionsChangesSuccess, {
                    entry: success.name,
                }),
            });
        },
    });
    const [permissions, setPermissions] = React.useState<PERMISSION[]>([]);
    const [disableSaveButton, setDisableSaveButton] = React.useState<boolean>(true);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        if (!data) return;
        setPermissions(data.permissions);
        setTitle(data.name);
        return removeTitle;
    }, [removeTitle, setTitle, data]);

    React.useEffect(() => {
        if (!data) return;
        const permissionsArraysEqual = (a: string[], b?: string[]) => {
            if (!b) return true;
            return a.length === b.length && a.every((val, i) => val === b[i]);
        };

        setDisableSaveButton(permissionsArraysEqual(permissions, data.permissions));
    }, [data, permissions]);

    const onSubmit = React.useCallback(() => {
        if (!data) return;
        mutate({ roleId, data: { name: data.name, permissions } });
    }, [data, mutate, permissions, roleId]);

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/roles`}>
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(rolesMessages.backToAllRoles)}
                </Link>
            </Button>

            <div className="relative mt-6 flex flex-1 flex-col gap-4 sm:gap-6">
                <PermissionBlock
                    title={formatMessage(customersMessages.customers)}
                    rolePermissions={permissions}
                    setRolePermissions={setPermissions}
                    permissionValues={Object.values(CUSTOMERS_PERMISSION)}
                />
                <PermissionBlock
                    title={formatMessage(rolesMessages.users)}
                    rolePermissions={permissions}
                    setRolePermissions={setPermissions}
                    permissionValues={Object.values(USERS_SECTIONS_PERMISSION)}
                />
                <PermissionBlock
                    title={formatMessage(subscriptionsMessages.subscriptions)}
                    rolePermissions={permissions}
                    setRolePermissions={setPermissions}
                    permissionValues={Object.values(SUBSCRIPTIONS_SECTION_PERMISSION)}
                />
                <PermissionBlock
                    title={formatMessage(discountsMessages.discounts)}
                    rolePermissions={permissions}
                    setRolePermissions={setPermissions}
                    permissionValues={Object.values(DISCOUNTS_SECTION_PERMISSION)}
                />

                <PermissionBlock
                    title={formatMessage(paymentsMessages.financials)}
                    rolePermissions={permissions}
                    setRolePermissions={setPermissions}
                    permissionValues={Object.values(FINANCIALS_SECTION_PERMISSION)}
                />
                <PermissionBlock
                    title={formatMessage(billingMessages.billingSettings)}
                    rolePermissions={permissions}
                    setRolePermissions={setPermissions}
                    permissionValues={Object.values(BILLING_SETTINGS_SECTION_PERMISSION)}
                />
            </div>

            <div className="mt-6 flex w-full justify-end">
                <Button onClick={onSubmit} disabled={disableSaveButton}>
                    <FormattedMessage {...rolesMessages.saveChanges} />
                </Button>
            </div>
        </>
    );
}

export default RoleDashboard;
